import { useMemo, useRef, useState } from "react";
import { useCrm } from "@/lib/crm";
import { api } from "@/lib/api";
import { MATERIALE_CATEGORIE, humanSize, dateIt } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, FileText, Film, Image as ImageIcon, Presentation, File as FileIcon, Download, Link2, Trash2, Eye, Plus, FolderOpen, Clock, ShieldOff, Copy } from "lucide-react";

const ACCEPT = ".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.ppt,.pptx,.mp4,.mov";

function iconFor(ct = "") {
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("presentation") || ct.includes("powerpoint")) return Presentation;
  if (ct.includes("pdf")) return FileText;
  if (ct.startsWith("text/")) return FileText;
  return FileIcon;
}

export default function Materiali() {
  const { materiali, refresh } = useCrm();
  const fileRef = useRef();
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [categoria, setCategoria] = useState("Immagini");
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("Tutte");
  const [shareItem, setShareItem] = useState(null);
  const [durata, setDurata] = useState("7");

  const categorie = ["Tutte", ...MATERIALE_CATEGORIE];
  const list = useMemo(
    () => (filter === "Tutte" ? materiali : materiali.filter((m) => m.categoria === filter)),
    [materiali, filter]
  );

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    if (!titolo) setTitolo(f.name.replace(/\.[^.]+$/, ""));
  };

  const doUpload = async () => {
    if (!file || !titolo.trim()) { toast.error("Seleziona un file e inserisci un titolo"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("titolo", titolo);
    fd.append("descrizione", descrizione);
    fd.append("categoria", categoria);
    setUploading(true);
    setProgress(0);
    try {
      await api.uploadMateriale(fd, (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)));
      toast.success("Materiale caricato");
      setOpen(false);
      setFile(null); setTitolo(""); setDescrizione(""); setCategoria("Immagini");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const del = async (m) => { await api.deleteMateriale(m.id); await refresh(); toast.success("Materiale eliminato"); };

  const copyText = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato — pronto da inviare al cliente");
    } catch {
      window.prompt("Copia il link:", url);
    }
  };

  const genShare = async (m) => {
    const res = await api.shareMateriale(m.id, Number(durata));
    await refresh();
    await copyText(api.materialeShareUrl(m.id, res.token));
  };

  const revoke = async (m) => {
    await api.revokeShare(m.id);
    await refresh();
    toast.success("Link revocato — non più accessibile");
  };

  const sm = shareItem ? (materiali.find((x) => x.id === shareItem.id) || shareItem) : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">Materiali condivisi</h1>
          <p className="text-slate-500 mt-1">Immagini, testi, slide, presentazioni e showreel da condividere con i clienti.</p>
        </div>
        <Button data-testid="upload-materiale-button" onClick={() => setOpen(true)} className="rounded-full gap-1.5">
          <Upload className="h-4 w-4" /> Carica materiale
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {categorie.map((c) => (
          <button key={c} data-testid={`materiale-filter-${c}`} onClick={() => setFilter(c)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors ${filter === c ? "bg-primary text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white"}`}>
            {c}
          </button>
        ))}
      </div>

      {list.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 p-12 text-center">
          <FolderOpen className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Nessun materiale{filter !== "Tutte" ? ` in "${filter}"` : ""}. Carica il primo file.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((m) => {
            const Icon = iconFor(m.content_type);
            const isImg = (m.content_type || "").startsWith("image/");
            const fileUrl = api.materialeFileUrl(m.id);
            return (
              <Card key={m.id} data-testid={`materiale-card-${m.id}`} className="overflow-hidden animate-fade-up group">
                <div className="h-40 bg-slate-100 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                  {isImg
                    ? <img src={fileUrl} alt={m.titolo} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    : <Icon className="h-12 w-12 text-slate-400" />}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 dark:text-white truncate">{m.titolo}</div>
                      <div className="text-[11px] text-slate-400 truncate">{m.original_filename} · {humanSize(m.size)}</div>
                    </div>
                    <Badge variant="outline" className="flex-shrink-0">{m.categoria}</Badge>
                  </div>
                  {m.descrizione && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{m.descrizione}</p>}
                  <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                    <a data-testid={`materiale-preview-${m.id}`} href={fileUrl} target="_blank" rel="noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-primary py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                      <Eye className="h-3.5 w-3.5" /> Anteprima
                    </a>
                    <a href={api.materialeFileUrl(m.id, true)}
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Scarica">
                      <Download className="h-4 w-4" />
                    </a>
                    <button data-testid={`materiale-copylink-${m.id}`} onClick={() => { setShareItem(m); setDurata("7"); }} title="Crea link condivisibile"
                      className={`inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 ${m.share_token ? "text-primary" : "text-slate-400 hover:text-primary"}`}>
                      <Link2 className="h-4 w-4" />
                    </button>
                    <button data-testid={`materiale-delete-${m.id}`} onClick={() => del(m)} title="Elimina"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-slate-400 hover:text-destructive hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Share dialog */}
      <Dialog open={!!shareItem} onOpenChange={(v) => !v && setShareItem(null)}>
        <DialogContent className="max-w-md" data-testid="materiale-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Link condivisibile</DialogTitle>
            <DialogDescription>{sm?.titolo} — genera un link da inviare al cliente, con scadenza opzionale.</DialogDescription>
          </DialogHeader>

          {sm && (
            <div className="grid gap-4 py-1">
              {sm.share_token ? (
                <div className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-500/10 p-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                    <Clock className="h-4 w-4" />
                    {sm.share_scadenza ? `Link attivo · scade il ${dateIt(sm.share_scadenza)}` : "Link attivo · senza scadenza"}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Input readOnly value={api.materialeShareUrl(sm.id, sm.share_token)} className="text-xs h-8 bg-white dark:bg-slate-900" data-testid="materiale-share-url" onFocus={(e) => e.target.select()} />
                    <Button size="sm" variant="outline" className="h-8 gap-1.5 flex-shrink-0" data-testid="materiale-copy-share-url" onClick={() => copyText(api.materialeShareUrl(sm.id, sm.share_token))}>
                      <Copy className="h-3.5 w-3.5" /> Copia
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">Nessun link attivo per questo materiale.</p>
              )}

              <div className="grid gap-1.5">
                <Label>Scadenza del link</Label>
                <Select value={durata} onValueChange={setDurata}>
                  <SelectTrigger data-testid="materiale-durata-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 giorno</SelectItem>
                    <SelectItem value="7">7 giorni</SelectItem>
                    <SelectItem value="30">30 giorni</SelectItem>
                    <SelectItem value="90">90 giorni</SelectItem>
                    <SelectItem value="0">Nessuna scadenza</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            {sm?.share_token && (
              <Button variant="outline" className="gap-1.5 text-destructive hover:text-destructive" data-testid="materiale-revoke-button" onClick={() => revoke(sm)}>
                <ShieldOff className="h-4 w-4" /> Revoca
              </Button>
            )}
            <Button className="gap-1.5" data-testid="materiale-genshare-button" onClick={() => genShare(sm)}>
              <Link2 className="h-4 w-4" /> {sm?.share_token ? "Rigenera e copia" : "Genera e copia"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload dialog */}
      <Dialog open={open} onOpenChange={(v) => !uploading && setOpen(v)}>
        <DialogContent className="max-w-md" data-testid="materiale-upload-dialog">
          <DialogHeader>
            <DialogTitle>Carica materiale</DialogTitle>
            <DialogDescription>Max 100MB · immagini, PDF, testi, slide, video.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); pickFile(e.dataTransfer.files?.[0]); }}
              className="rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-6 text-center cursor-pointer hover:border-primary/50 transition-colors">
              <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">{file ? file.name : "Trascina qui o clicca per scegliere"}</p>
              {file && <p className="text-xs text-slate-400 mt-1">{humanSize(file.size)}</p>}
              <input ref={fileRef} data-testid="materiale-file-input" type="file" accept={ACCEPT} className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
            </div>
            <div className="grid gap-1.5"><Label>Titolo *</Label><Input data-testid="materiale-titolo-input" value={titolo} onChange={(e) => setTitolo(e.target.value)} /></div>
            <div className="grid gap-1.5">
              <Label>Categoria</Label>
              <Select value={categoria} onValueChange={setCategoria}>
                <SelectTrigger data-testid="materiale-categoria-select"><SelectValue /></SelectTrigger>
                <SelectContent>{MATERIALE_CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Descrizione</Label><Textarea value={descrizione} onChange={(e) => setDescrizione(e.target.value)} rows={2} /></div>
            {uploading && (
              <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div className="h-full bg-primary transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Annulla</Button>
            <Button data-testid="materiale-upload-submit" onClick={doUpload} disabled={uploading}>{uploading ? `Caricamento ${progress}%` : "Carica"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
